import React, { useEffect, useState } from "react";
import Link from "next/link";
import { PostItemProps } from "@/types/modules/post/components";
import { AuthorType } from "@/core/types/contentTypes";
import { TinyBookmarkButton } from "@/components/ui-kit/Buttons/TinyBookmarkButton";
import { DottedSeparator } from "@/components/ui-kit/Decorations/DottedSeparator";
import { ImageWithSkeleton } from "@/components/ui-kit/ImageWithSkeleton";
import { TinyDownloadButton } from "@/components/ui-kit/Buttons/TinyDownloadButton";
import styles from "@/styles/post.module.css";
import { TinyPlayAudioButton } from "@/components/ui-kit/Buttons/Audio/TinyPlayAudioButton";
import { TinyPauseAudioButton } from "@/components/ui-kit/Buttons/Audio/TinyPauseAudioButton";
import { useAudioPlayer } from "@/hooks/useAudioPlayer";
import { useOfflinePostContent } from "@/hooks/indexeddb/useOfflinePostContent";
import { db } from "@/db";
import { useLiveQuery } from "dexie-react-hooks";
import { addQueryParamToUrl } from "@/utils/addQueryParamToUrl";

export const PostItem: React.FC<PostItemProps> = ({
  post,
  withoutImage,
  isStandaloneGallery,
  homePagePostItem = false,
  fromIssueId = null,
  imgQuality = 75,
}) => {
  const [hoveredPost, setHoveredPost] = useState<boolean>(false);
  const [offlineThumbnailBlob, setOfflineThumbnailBlob] = useState<string>();
  const { saveOrDelete, downloading, offlinePostThumbnail } = useOfflinePostContent();
  const [isPostDownloaded, setIsPostDownloaded] = useState<boolean>(false);
  const { setTrack, play, pause, isPaused, currentTrack } = useAudioPlayer();
  const isPlaying = currentTrack?.id === post.id;
  const isPostInDb = useLiveQuery(() => db.posts.get(post.id));
  const isPostAdvertorial = post.authorsSearch.includes("Advertorial");

  const getPostLink = () => {
    if (isStandaloneGallery) {
      return `/galerie/${post.id}/${post.slug}`;
    }

    let localLink = post.local_link;

    if (fromIssueId) {
      // add query param so we know we are listing in issue posts
      localLink = addQueryParamToUrl(localLink, "i", "1");
    }

    return localLink;
  };

  useEffect(() => {
    if (isPostInDb && isPostInDb.id) {
      setIsPostDownloaded(true);
      return;
    }
    setIsPostDownloaded(false);
  }, [isPostInDb]);

  useEffect(() => {
    offlinePostThumbnail(post).then((res) => {
      setOfflineThumbnailBlob(res);
    });
  }, [post, offlinePostThumbnail]);

  const renderList = (list: AuthorType[]) => {
    return (
      <>
        {!!list.length && (
          <span className="mt-1 flex flex-wrap">
            {list.map((i, key) => {
              return (
                <span className="overflow flex items-center break-words break-all" key={i.slug}>
                  <Link
                    href={i.link}
                    className={`font-condensed font-light text-dark no-underline decoration-transparent decoration-1 underline-offset-4 transition-colors ease-in-out hover:decoration-dark-70 dark:text-white dark:hover:decoration-white-70 md:underline ${
                      homePagePostItem ? "text-2xs" : "text-base"
                    }`}
                  >
                    {i.name}
                  </Link>
                  <span className="mr-1 text-dark dark:text-white">{key + 1 !== list.length ? "," : ""}</span>
                </span>
              );
            })}
          </span>
        )}
      </>
    );
  };

  return (
    <>
      <div className={`${homePagePostItem ? "" : "py-[24px] lg:py-[16px]"} flex justify-between`}>
        <div className="flex w-full">
          <div className="post-item-body inline-flex w-full flex-col items-start">
            <div className="flex w-full items-start">
              <div className="flex-1">
                <Link
                  href={getPostLink()}
                  className={`no-underline decoration-1 underline-offset-4 transition-colors ease-in-out visited:text-dark-visited hover:decoration-dark-70 dark:hover:decoration-white-70 md:underline ${
                    hoveredPost ? "decoration-dark-70 dark:decoration-white-70" : "decoration-transparent"
                  }`}
                  onMouseEnter={() => setHoveredPost(true)}
                  onMouseLeave={() => setHoveredPost(false)}
                >
                  <h3
                    className={`${
                      homePagePostItem ? "mb-[4px]" : "mb-2.5 text-xl sm:text-3xl"
                    } text-dark dark:text-white`}
                  >
                    {isStandaloneGallery ? `Fotogalerie: ${post.title}` : post.title}
                  </h3>
                </Link>

                {/* Authors/advertorial */}
                {isPostAdvertorial ? (
                  <span
                    className={`font-condensed font-light text-dark no-underline decoration-transparent decoration-1 underline-offset-4 transition-colors ease-in-out hover:decoration-dark-70 dark:text-white dark:hover:decoration-white-70 md:underline ${
                      homePagePostItem ? "text-2xs" : "text-base"
                    }`}
                  >
                    ADVERTORIAL
                  </span>
                ) : (
                  renderList(post.authors)
                )}

                {homePagePostItem && (
                  <div className="mt-[8px] flex items-center [&>*:nth-child(n+2)]:ml-[4px]">
                    {post.audio?.url && (
                      <>
                        {!isPaused && isPlaying ? (
                          <TinyPauseAudioButton
                            short
                            onClick={async () => {
                              await pause();
                            }}
                          />
                        ) : (
                          <TinyPlayAudioButton
                            short
                            onClick={async () => {
                              await setTrack(post);
                              return;
                            }}
                          />
                        )}
                      </>
                    )}
                    <TinyBookmarkButton postTitle={post?.title} postId={post.id} short />
                    <TinyDownloadButton
                      short
                      onClick={() => saveOrDelete(post)}
                      downloaded={isPostDownloaded}
                      downloading={downloading}
                      postTitle={post.title}
                      mediaURL={post.audio?.url}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="ml-4">
            {post?.thumbnail["full"]?.url && !withoutImage && (
              <Link
                className={`${!homePagePostItem ? "md:w-[160px]" : "md:w-[116px]"} flex w-[116px] overflow-hidden`}
                href={getPostLink()}
              >
                <ImageWithSkeleton
                  src={offlineThumbnailBlob ?? post?.thumbnail["full"]?.url!}
                  alt={isStandaloneGallery ? `Fotogalerie: ${post.title}` : post.title}
                  width={!homePagePostItem ? 160 : 116}
                  height={!homePagePostItem ? 135 : 87}
                  className="aspect-[4/3] object-cover"
                  onMouseEnter={() => setHoveredPost(true)}
                  onMouseLeave={() => setHoveredPost(false)}
                  isHovered={hoveredPost}
                  quality={imgQuality}
                />
              </Link>
            )}
            {!homePagePostItem && (
              <div className={styles.relatedPostButtonsWrap}>
                {post.audio?.url && (
                  <>
                    {!isPaused && isPlaying ? (
                      <TinyPauseAudioButton
                        onClick={async () => {
                          await pause();
                        }}
                      />
                    ) : (
                      <TinyPlayAudioButton
                        onClick={async () => {
                          await setTrack(post);
                          return;
                        }}
                      />
                    )}
                  </>
                )}
                <TinyBookmarkButton postTitle={post?.title} postId={post.id} />
                <TinyDownloadButton
                  onClick={() => saveOrDelete(post)}
                  downloaded={isPostDownloaded}
                  downloading={downloading}
                  postTitle={post.title}
                  mediaURL={post.audio?.url}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {!homePagePostItem && <DottedSeparator />}
    </>
  );
};
